import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckboxHolder,
  LabelSort,
  StyledFilterWindow,
  StyledFilterWindowBody,
  StyledFilterWindowButtonSortHolder,
  StyledFilterWindowFooter,
  StyledFilterWindowSort,
  StyledFilterWindowTitle,
} from '../../ui/pageWithTable/styled';
import {
  NavigationAndActionArrowAscending,
  NavigationAndActionArrowDescending,
  NavigationAndActionClose,
  NavigationAndActionDelete,
} from '../../ui/icons';
import { ToggleButton } from '../../ui/toggleButton';
import { Button, ButtonType } from '../../ui/button';
import { Label } from '../../ui/typography';
import { DatePicker, Input } from '../../ui/input';
import { TableColumn } from '../../ui/table';
import { SortType } from '../../types/PagingParams';
import {Checkbox} from '../../ui';
import {
  deleteBillColumnFilters,
  Filter,
  FilterCheckbox,
  setColumnFilter,
  setFilterCheckboxState,
} from '../redux/slice';
import { useAppDispatch } from '../../hooks/appHooks';
import {useTheme} from "styled-components";

export interface FilterWindowProps {
  tableFilters?: Filter[];
  showCalendar?: boolean;
  showCheckbox?: boolean;
  showSearch?: boolean;
  column: TableColumn;
  onClose: () => void;
  onSortAscButtonClick: (column: TableColumn, sortType: SortType) => void;
  onSortDescButtonClick: (column: TableColumn, sortType: SortType) => void;
  fetchColumnCheckboxes?: () => void;
  checkboxFilters?: Filter[];
  showSorting?: (accessor: string) => boolean;
}

export const FilterWindow: FC<FilterWindowProps> = ({
  tableFilters,
  showCalendar,
  showCheckbox,
  showSearch,
  column,
  onClose,
  onSortAscButtonClick,
  onSortDescButtonClick,
  fetchColumnCheckboxes,
  checkboxFilters,
  showSorting,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [fromDate, setFromDate] = useState<string>(
    tableFilters
      ?.find((filter) => filter.column === column.label)
      ?.filters.dateFrom?.toLocaleDateString('hr') ?? '',
  );
  const [toDate, setToDate] = useState<string>(
    tableFilters
      ?.find((filter) => filter.column === column.label)
      ?.filters.dateTo?.toLocaleDateString('hr') ?? '',
  );
  const [searchValue, setSearchValue] = useState<string>(
    tableFilters?.find((filter) => filter.column === column.label)?.filters
      .search ?? '',
  );
  const [checkboxes, setCheckboxes] = useState<FilterCheckbox[]>(
    checkboxFilters?.find((filter) => filter.column === column.label)?.filters
      .checkbox ?? [],
  );
  const theme = useTheme() as any;

  const colorBasedOnSortChange = (sortType: SortType) => {
    if (column.sort === SortType.DESC && sortType === SortType.DESC)
      return theme.colors.primaryT2;
    if (column.sort === SortType.ASC && sortType === SortType.ASC)
      return theme.colors.primaryT2;
    return theme.colors.primaryK8;
  };

  const applyFilterButtonClick = () => {
    if (
      !fromDate &&
      !toDate &&
      !searchValue &&
      !checkboxes.filter((checkbox) => checkbox.checked).length
    ) {
      dispatch(deleteBillColumnFilters(column.label));
    } else {
      const d1 = toDate.split('.').map(Number);
      const d2 = fromDate.split('.').map(Number);
      dispatch(
        setColumnFilter({
          column: column.label,
          filters: {
            dateFrom: fromDate
              ? new Date(Date.UTC(d2[2], d2[1] - 1, d2[0]))
              : undefined,
            dateTo: toDate
              ? new Date(Date.UTC(d1[2], d1[1] - 1, d1[0]))
              : undefined,
            search: searchValue ?? undefined,
            checkbox: checkboxes.filter((checkbox) => checkbox.checked),
          },
        }),
      );
    }

    onClose();
  };

  const deleteFilterButtonClick = () => {
    const columnFilter = tableFilters?.find(
      (filter) => filter.column === column.label,
    )?.filters;
    if (columnFilter) dispatch(deleteBillColumnFilters(column.label));

    onClose();
  };

  const toggleCheckbox = (isChecked: boolean, value: string) => {
    dispatch(
      setFilterCheckboxState({
        column: column.label,
        checkbox: value,
        value: isChecked,
      }),
    );
  };

  useEffect(() => {
    if (showCheckbox && fetchColumnCheckboxes) fetchColumnCheckboxes();
  }, []);

  useEffect(() => {
    setCheckboxes(
      checkboxFilters?.find((filter) => filter.column === column.label)?.filters
        .checkbox ?? [],
    );
  }, [checkboxFilters]);

  const shouldHaveFooter = showSearch || showCheckbox || showCalendar;

  return (
    <StyledFilterWindow>
      <StyledFilterWindowTitle>
        <Label>{column.label}</Label>
        <ToggleButton onClick={onClose} icon={<NavigationAndActionClose />} />
      </StyledFilterWindowTitle>
      <StyledFilterWindowBody>
        {showSorting && showSorting(column.accessor) ? (
          <StyledFilterWindowSort>
            <LabelSort>{t('billsPage.billUtil.sorting')}</LabelSort>
            <StyledFilterWindowButtonSortHolder>
              <Button
                type={ButtonType.Filled}
                text={t('billsPage.billUtil.sortingAscending')}
                icon={<NavigationAndActionArrowAscending />}
                onClick={() => onSortAscButtonClick(column, SortType.ASC)}
                color={colorBasedOnSortChange(SortType.ASC)}
              />
              <Button
                type={ButtonType.Filled}
                text={t('billsPage.billUtil.sortingDescending')}
                icon={<NavigationAndActionArrowDescending />}
                onClick={() => onSortDescButtonClick(column, SortType.DESC)}
                color={colorBasedOnSortChange(SortType.DESC)}
              />
            </StyledFilterWindowButtonSortHolder>
          </StyledFilterWindowSort>
        ) : (
          <div />
        )}
        {showCalendar ? (
          <div>
            <LabelSort>{t('billsPage.billUtil.date')}</LabelSort>
            <DatePicker
              label={t('billsPage.billUtil.from')}
              value={fromDate}
              onChange={setFromDate}
            />
            <DatePicker
              label={t('billsPage.billUtil.to')}
              value={toDate}
              onChange={setToDate}
            />
          </div>
        ) : (
          <div />
        )}
        {showSearch ? (
          <div>
            <LabelSort>{t('billsPage.billUtil.search')}</LabelSort>
            <Input
              type="search"
              defaultValue={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholderText={t('billsPage.billUtil.searchHelperText')}
              onClear={() => {}}
            />
          </div>
        ) : (
          <div />
        )}
        {showCheckbox ? (
          <CheckboxHolder>
            {checkboxes.map((checkbox) => (
              <Checkbox
                key={checkbox.value}
                text={checkbox.value ?? ''}
                onCheckboxClicked={(isChecked) =>
                  toggleCheckbox(isChecked, checkbox.value ?? '')
                }
                shouldBeChecked={checkbox.checked}
              />
            ))}
          </CheckboxHolder>
        ) : (
          <div />
        )}
      </StyledFilterWindowBody>
      {shouldHaveFooter ? (
        <StyledFilterWindowFooter>
          <Button
            type={ButtonType.Action}
            text={t('billsPage.billUtil.delete')}
            icon={<NavigationAndActionDelete />}
            onClick={deleteFilterButtonClick}
          />
          <Button
            type={ButtonType.Primary}
            text={t('billsPage.billUtil.apply')}
            onClick={applyFilterButtonClick}
          />
        </StyledFilterWindowFooter>
      ) : (
        <div />
      )}
    </StyledFilterWindow>
  );
};
